/**
 * FRONT-765 — pure decision logic for stopping a chat turn's background-chat
 * run, extracted so it's unit-testable without mounting a Chat/useAgentChat
 * instance (same "extract for testability" precedent as
 * `promote-on-close-logic.ts` / `queued-send.ts`).
 *
 * Two halves:
 *  - `extractBackgroundChatRunSignal` reads the AI-SDK's transient
 *    custom-data chunk (see `backgroundChatRunChunk` in
 *    src/agents/streams/background-run-frame.ts) that hands the client the
 *    live turn's runId right at turn start. Defensive against a malformed or
 *    foreign `data-*` chunk — `data` is `unknown` from the SDK's own typing.
 *
 *    PR-panel finding: a surface that keeps ONE `useAgentChat` instance
 *    mounted across a session switch (pwa-shell.tsx) never aborts the OLD
 *    session's still-open stream — the AI SDK's `useChat` routes every Chat
 *    instance's callbacks through one per-call-site ref refreshed every
 *    render, so a chunk from an orphaned previous-session stream still
 *    reaches the CURRENT render's `onData`. The chunk carries the session it
 *    was minted for; the caller (`use-agent-chat.ts`) compares it against
 *    the CURRENT session and discards a mismatch — this function only reads
 *    the chunk, the comparison happens where the current session is known.
 *  - `resolveStopRunId` picks which runId a Stop press should cancel: the
 *    live SSE-delivered one when we have it (fresher — it arrives right at
 *    turn start), falling back to the session's polled background-chat row
 *    (`useBackgroundChatForSession`) for the brief window before the live
 *    value has arrived.
 */
export function extractBackgroundChatRunSignal(
  dataPart: { type: string; data?: unknown },
): { runId: string; sessionId: string } | null {
  if (dataPart.type !== 'data-background-chat-run') return null;
  const data = dataPart.data as { runId?: unknown; sessionId?: unknown } | undefined;
  if (typeof data?.runId !== 'string' || typeof data?.sessionId !== 'string') return null;
  return { runId: data.runId, sessionId: data.sessionId };
}

export function resolveStopRunId(params: {
  liveRunId: string | null;
  fallbackRunId: string | null | undefined;
}): string | null {
  return params.liveRunId ?? params.fallbackRunId ?? null;
}

/**
 * PR-panel finding: a caller's polled `useBackgroundChatForSession` row can
 * itself be momentarily stale — it updates over a separate SSE-invalidate
 * round trip with no ordering guarantee against the live chunk, so right
 * after a new turn starts it can still show the PREVIOUS (already-terminal)
 * turn's row. A terminal run is never a valid cancel target (the server only
 * flips rows still `status: "running"`), so only ever offer a fallback whose
 * own row says it's actually running — never one already known to be
 * completed/failed/cancelled.
 */
export function runningFallbackRunId(
  bgRun: { runId: string; status: string } | null | undefined,
): string | undefined {
  return bgRun?.status === 'running' ? bgRun.runId : undefined;
}
